import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';

function formatNumber(num: number): string {
  if (num >= 10000) return (num / 10000).toFixed(1) + '万';
  return num.toString();
}

export default function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const videoId = parseInt(id || '0');
  const video = useMemo(() => getVideoById(videoId), [videoId]);
  const relatedVideos = useMemo(() => getRelatedVideos(videoId), [videoId]);
  const [isLiked, setIsLiked] = useState(false);
  const [isCollected, setIsCollected] = useState(false);

  if (!video) {
    return (
      <>
        <SEOHead
          title="视频未找到 - 91视频网"
          description="抱歉，您要查看的视频不存在或已下架。"
          keywords="91视频网"
          canonicalUrl={`https://www.91video.com/video/${videoId}`}
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-error-warning-line text-3xl"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">视频未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的视频不存在或已下架</p>
              <Link
                to="/"
                className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const pageTitle = `${video.title} - 91视频网`;
  const pageDesc = video.description;
  const pageKeywords = `91视频网,${video.tags.join(',')},${video.categoryName}视频,在线观看`;

  const videoSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title,
    description: video.description,
    thumbnailUrl: video.thumbnail,
    uploadDate: video.publishDate,
    duration: `PT${video.duration.replace(':', 'M')}S`,
    author: {
      '@type': 'Person',
      name: video.author,
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://www.91video.com/video/${video.id}`}
        ogType="video.other"
        schema={videoSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: video.categoryName, href: `/category/${video.categoryId}` },
                  { label: video.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Video Player Area */}
                <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                    title={`${video.title} - 91视频网`}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 transition-all duration-300 hover:scale-110 cursor-pointer">
                      <i className="ri-play-fill text-background-50 text-3xl md:text-4xl ml-1"></i>
                    </div>
                  </div>
                  <span className="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/75 text-background-50 text-xs font-medium">
                    {video.duration}
                  </span>
                  <span className="absolute top-3 left-3 px-2 py-1 rounded bg-primary-500/90 text-background-50 text-xs font-medium">
                    {video.quality}
                  </span>
                </div>

                {/* Video Info */}
                <div className="mt-4">
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                    {video.title}
                  </h1>

                  {/* Meta Bar */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img
                          src={video.authorAvatar}
                          alt={video.author}
                          className="w-full h-full object-cover"
                          title={video.author}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{video.author}</p>
                        <p className="text-xs text-foreground-500">{video.publishDate} 发布</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsLiked(!isLiked)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          isLiked
                            ? 'bg-primary-100 text-primary-600'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
                        {formatNumber(video.likes + (isLiked ? 1 : 0))}
                      </button>
                      <button
                        onClick={() => setIsCollected(!isCollected)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          isCollected
                            ? 'bg-accent-100 text-accent-700'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        <i className={`${isCollected ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-base`}></i>
                        收藏
                      </button>
                      <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer">
                        <i className="ri-share-forward-line text-base"></i>
                        分享
                      </button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-foreground-500 mb-4 flex-wrap">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>{formatNumber(video.views)} 次观看
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-1-line"></i>评论区
                    </span>
                  </div>

                  {/* Description */}
                  <div className="bg-background-100 rounded-xl p-4 md:p-5 mb-6">
                    <p className="text-sm text-foreground-700 leading-relaxed">{video.description}</p>
                  </div>

                  {/* Tags */}
                  <div className="flex items-center gap-2 flex-wrap mb-6">
                    {video.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:w-[360px] flex-shrink-0">
                <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">
                  相关推荐
                </h3>
                <div className="space-y-3">
                  {relatedVideos.map((rv) => (
                    <Link
                      key={rv.id}
                      to={`/video/${rv.id}`}
                      className="flex gap-3 group cursor-pointer"
                    >
                      <div className="relative w-40 flex-shrink-0 aspect-video rounded-lg overflow-hidden bg-background-200">
                        <img
                          src={rv.thumbnail}
                          alt={rv.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          title={rv.title}
                        />
                        <span className="absolute bottom-1 right-1 px-1 py-0.5 rounded bg-black/75 text-background-50 text-xs">
                          {rv.duration}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors mb-1">
                          {rv.title}
                        </h4>
                        <p className="text-xs text-foreground-500">{rv.author}</p>
                        <p className="text-xs text-foreground-400 mt-1">
                          {formatNumber(rv.views)} 次观看 · {rv.publishDate}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}